import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaFileInvoice,
  FaDownload,
  FaPrint,
  FaEnvelope,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaBuilding,
  FaUser,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope as FaEmailIcon
} from 'react-icons/fa';
import { invoiceService } from '../services/invoiceService';
import { formatPrice } from '../utils/currency';
import { formatInvoiceDate, calculateInvoiceTotals, getInvoiceStatusBadge } from '../utils/invoice';
import { toast } from 'react-toastify';
import logo from '../assets/img/logo/logo-dark.svg';

const Invoice = ({ order, invoiceData = null, onClose = null }) => {
  const [invoice, setInvoice] = useState(invoiceData);
  const [loading, setLoading] = useState(!invoiceData);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (!invoiceData && order) {
      fetchInvoice();
    }
  }, [order, invoiceData]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getInvoiceByOrder(order.id || order.orderId);
      setInvoice(data);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      // Generate invoice on the fly if not found
      generateInvoiceFromOrder();
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceFromOrder = () => {
    if (!order) return;

    const luckyDrawTickets = Array.isArray(order.luckyDrawTickets) ? order.luckyDrawTickets : [];
    const luckyDrawAmount = luckyDrawTickets.reduce((sum, t) => sum + (Number(t.ticketPrice) || 0) * (Number(t.quantity) || 0), 0);

    const totals = calculateInvoiceTotals(
      order.items || [],
      order.deliveryCharge || 0,
      order.platformFee || 0,
      order.discount || 0,
      true,
      18,
      luckyDrawAmount
    );

    const invoiceNumber = `INV-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${String(Math.floor(Math.random() * 10000)).padStart(5, '0')}`;

    setInvoice({
      invoiceNumber,
      orderId: order.id || order.orderId,
      date: order.date || order.createdAt || new Date().toISOString(),
      status: order.paymentStatus === 'Paid' ? 'paid' : 'pending',
      customer: {
        name: order.shippingAddress?.name || 'Customer',
        email: order.shippingAddress?.email || '',
        phone: order.shippingAddress?.mobile || '',
        address: order.shippingAddress?.address || order.shippingAddress?.fullAddress || '',
        city: order.shippingAddress?.city || '',
        state: order.shippingAddress?.state || '',
        pincode: order.shippingAddress?.postalCode || '',
        gstin: order.shippingAddress?.gstin || ''
      },
      items: order.items || [],
      luckyDrawTickets,
      luckyDrawAmount: totals.luckyDrawAmount,
      subtotal: totals.subtotal,
      deliveryCharge: totals.deliveryCharge,
      platformFee: totals.platformFee,
      discount: totals.discount,
      gst: totals.gst,
      total: totals.grandTotal,
      paymentMethod: order.paymentMethod || 'N/A',
      paymentStatus: order.paymentStatus || 'Pending'
    });
  };

  const handleDownloadPDF = async () => {
    try {
      if (!invoice) return;
      const element = document.getElementById('invoice-content');
      if (!element) {
        toast.error('Invoice content not found');
        return;
      }
      setDownloadingPdf(true);
      let html2pdf;
      try {
        html2pdf = (await import('html2pdf.js')).default;
      } catch (e) {
        toast.error('PDF library not loaded. Run: npm install html2pdf.js');
        return;
      }
      const opt = {
        margin: 10,
        filename: `invoice-${invoice.invoiceNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };
      await html2pdf().set(opt).from(element).save();
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download invoice PDF: ' + (error.message || 'Unknown error'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendEmail = async () => {
    try {
      if (!invoice) return;
      setSendingEmail(true);
      await invoiceService.sendInvoiceEmail(invoice.invoiceNumber);
      toast.success('Invoice sent to email successfully!');
    } catch (error) {
      toast.error('Failed to send invoice email: ' + error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center p-20">
        <FaTimesCircle className="text-4xl text-red-500 mx-auto mb-4" />
        <p className="text-gray-600">Invoice not found</p>
      </div>
    );
  }

  const statusBadge = getInvoiceStatusBadge(invoice.status);

  return (
    <div className="bg-white invoice-print-root">
      {/* Action Bar - Hidden on Print */}
      <div className="no-print bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <FaFileInvoice className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Tax Invoice</h2>
            <p className="text-sm text-gray-600">Invoice #{invoice.invoiceNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
            {statusBadge.label}
          </span>
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            title="Send via Email"
          >
            {sendingEmail ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
            Email
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={downloadingPdf}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            title="Download PDF"
          >
            {downloadingPdf ? <FaSpinner className="animate-spin" /> : <FaDownload />}
            Download
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            title="Print"
          >
            <FaPrint /> Print
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Invoice Content - same structure for screen, print, and email/PDF */}
      <div id="invoice-content" className="p-6 md:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="border-b-2 border-gray-900 pb-4 mb-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <img loading="lazy" src={logo} alt="Sparepe" className="h-12 w-auto object-contain" />
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-900">Sparepe</h1>
                  <p className="text-xs md:text-sm text-gray-600">Automotive Parts & Services</p>
                </div>
              </div>
              <div className="mt-3 text-xs md:text-sm text-gray-600 space-y-0.5">
                <p>123 Business Street, Industrial Area</p>
                <p>Mumbai, Maharashtra 400001, India</p>
                <p><strong>GSTIN:</strong> 27ABCDE1234F1Z5 &nbsp; <strong>PAN:</strong> ABCDE1234F</p>
              </div>
            </div>
            <div className="text-right">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">TAX INVOICE</h2>
              <p className="text-sm text-gray-600 mb-1">
                <strong>Invoice No:</strong> {invoice.invoiceNumber}
              </p>
              <p className="text-sm text-gray-600 mb-1">
                <strong>Date:</strong> {formatInvoiceDate(invoice.date)}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Order ID:</strong> {invoice.orderId}
              </p>
            </div>
          </div>
        </div>

        {/* Billing & Shipping Address */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FaUser className="text-gray-500" />
              Billed To
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-semibold text-gray-900 mb-1">{invoice.customer?.name || 'Customer'}</p>
              <p className="text-sm text-gray-600 mb-1">{invoice.customer?.address || ''}</p>
              <p className="text-sm text-gray-600 mb-1">
                {invoice.customer?.city && `${invoice.customer.city}, `}
                {invoice.customer?.state && `${invoice.customer.state} - `}
                {invoice.customer?.pincode}
              </p>
              {invoice.customer?.gstin && (
                <p className="text-sm text-gray-600 mt-2">
                  <strong>GSTIN:</strong> {invoice.customer.gstin}
                </p>
              )}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FaMapMarkerAlt className="text-gray-500" />
              Ship To
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-semibold text-gray-900 mb-1">{invoice.customer?.name || 'Customer'}</p>
              <p className="text-sm text-gray-600 mb-1">{invoice.customer?.address || ''}</p>
              <p className="text-sm text-gray-600 mb-1">
                {invoice.customer?.city && `${invoice.customer.city}, `}
                {invoice.customer?.state && `${invoice.customer.state} - `}
                {invoice.customer?.pincode}
              </p>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-900 text-white">
                <th className="px-3 py-2 text-left text-xs font-semibold">S.No.</th>
                <th className="px-3 py-2 text-left text-xs font-semibold">Item Description</th>
                <th className="px-3 py-2 text-center text-xs font-semibold">HSN/SAC</th>
                <th className="px-3 py-2 text-center text-xs font-semibold">Qty</th>
                <th className="px-3 py-2 text-right text-xs font-semibold">Rate</th>
                <th className="px-3 py-2 text-right text-xs font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items?.map((item, index) => {
                const itemPrice = item.discountPrice || item.price;
                const itemTotal = itemPrice * (item.quantity || 1);
                return (
                  <tr key={`item-${index}`} className="border-b border-gray-200">
                    <td className="px-3 py-2 text-gray-700">{index + 1}</td>
                    <td className="px-3 py-2 text-gray-900">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        {item.brand && <p className="text-xs text-gray-500">Brand: {item.brand}</p>}
                        {item.partNumber && <p className="text-xs text-gray-500">Part No: {item.partNumber}</p>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center text-gray-600">8708.99.90</td>
                    <td className="px-3 py-2 text-center text-gray-700">{item.quantity || 1}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{formatPrice(itemPrice)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatPrice(itemTotal)}</td>
                  </tr>
                );
              })}
              {invoice.luckyDrawTickets?.map((ticket, index) => {
                const amt = (Number(ticket.ticketPrice) || 0) * (Number(ticket.quantity) || 1);
                return (
                  <tr key={`lucky-${index}`} className="border-b border-gray-200">
                    <td className="px-3 py-2 text-gray-700">{(invoice.items?.length || 0) + index + 1}</td>
                    <td className="px-3 py-2 text-gray-900">
                      <p className="font-medium">Lucky Draw: {ticket.drawTitle || 'Lucky Draw Ticket'}</p>
                    </td>
                    <td className="px-3 py-2 text-center text-gray-600">9997.19</td>
                    <td className="px-3 py-2 text-center text-gray-700">{ticket.quantity || 1}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{formatPrice(ticket.ticketPrice || 0)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatPrice(amt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-4">
          <div className="w-full max-w-sm">
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal:</span>
                <span>{formatPrice(invoice.subtotal || 0)}</span>
              </div>
              {(invoice.deliveryCharge || 0) > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Delivery Charge:</span>
                  <span>{formatPrice(invoice.deliveryCharge)}</span>
                </div>
              )}
              {(invoice.platformFee || 0) > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Platform Fee:</span>
                  <span>{formatPrice(invoice.platformFee)}</span>
                </div>
              )}
              {(invoice.luckyDrawAmount || 0) > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Lucky Draw:</span>
                  <span>{formatPrice(invoice.luckyDrawAmount)}</span>
                </div>
              )}
              {(invoice.discount || 0) > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Discount:</span>
                  <span>-{formatPrice(invoice.discount)}</span>
                </div>
              )}
              <div className="border-t border-gray-300 pt-2 mt-2">
                <div className="flex justify-between text-gray-700 mb-1">
                  <span>Total (Before GST):</span>
                  <span>{formatPrice((invoice.subtotal || 0) + (invoice.deliveryCharge || 0) + (invoice.platformFee || 0) + (invoice.luckyDrawAmount || 0) - (invoice.discount || 0))}</span>
                </div>
                {invoice.gst != null && (typeof invoice.gst === 'object' ? (
                  <>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>CGST (9%):</span>
                      <span>{formatPrice(invoice.gst.cgst || 0)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>SGST (9%):</span>
                      <span>{formatPrice(invoice.gst.sgst || 0)}</span>
                    </div>
                    {invoice.gst.igst > 0 && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>IGST:</span>
                        <span>{formatPrice(invoice.gst.igst)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold text-gray-700 mt-1">
                      <span>Total GST:</span>
                      <span>{formatPrice(invoice.gst.totalGST || 0)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>GST (18%):</span>
                    <span>{formatPrice(Number(invoice.gst) || 0)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t-2 border-gray-900 pt-3 mt-3">
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>Grand Total:</span>
                  <span>{formatPrice(invoice.total || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="border-t border-gray-200 pt-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Payment Information</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Method:</strong> {invoice.paymentMethod}</p>
                <p><strong>Status:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded text-xs ${
                    invoice.paymentStatus === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {invoice.paymentStatus}
                  </span>
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Terms & Conditions</h3>
              <div className="text-xs text-gray-600 space-y-1">
                <p>• Goods once sold will not be taken back</p>
                <p>• Subject to Mumbai jurisdiction</p>
                <p>• E. & O.E.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 pt-4 text-center text-xs text-gray-500">
          <p>This is a computer-generated invoice and does not require a signature.</p>
          <p className="mt-2">Thank you for your business!</p>
        </div>
      </div>

      {/* Print Styles - only invoice content visible when printing; no extra blank page */}
      <style>{`
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            min-height: 0 !important;
            overflow: hidden !important;
          }
          body * {
            visibility: hidden;
            height: 0 !important;
            max-height: 0 !important;
            overflow: hidden !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
          }
          .invoice-print-root,
          .invoice-print-root #invoice-content,
          .invoice-print-root #invoice-content * {
            visibility: visible !important;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          .invoice-print-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            right: 0 !important;
            background: white !important;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          .invoice-print-root .no-print {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          #invoice-content {
            position: static !important;
            padding: 16px !important;
            page-break-after: avoid !important;
          }
          @page {
            margin: 1cm;
            size: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default Invoice;


